import React from 'react';
import PixelIcons from '../PixelIcons.jsx';
import { useT } from '../../i18n/useT.js';

// Arcade.jsx — Global arcade store + floating coins + playable mini-games
const Arcade = (() => {
  const { Coin, Heart, Joystick, Star, Diamond, Bolt, Skull, Arrow } = PixelIcons;

  // =====================================================================
  // GLOBAL STORE (event-emitter pattern, like useTweaks)
  // =====================================================================
  const listeners = new Set();
  let state = {
    lives: 3,
    score: 0,
    highScore: 0,
    turbo: false,
    turboMs: 0,
    screen: "select",     // "select" | "playing" | "gameover"
    currentGame: null,    // "invaders" | "catcher"
  };
  let _restartTimer = null;

  function _commit(next) {
    state = { ...state, ...next };
    listeners.forEach((cb) => cb(state));
  }
  function get() { return state; }

  function useArcade() {
    const [s, setS] = React.useState(state);
    React.useEffect(() => {
      const cb = (n) => setS(n);
      listeners.add(cb);
      return () => listeners.delete(cb);
    }, []);
    return s;
  }

  // -------- Tiny synth sfx --------
  let _audioCtx = null;
  function _ctx() {
    if (_audioCtx) return _audioCtx;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      _audioCtx = new AC();
    } catch (e) {}
    return _audioCtx;
  }
  function beep(f = 600, dur = 0.08, type = "square", vol = 0.12) {
    const ctx = _ctx(); if (!ctx) return;
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = type; o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g).connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + dur + 0.02);
  }

  // -------- Actions --------
  // (coins removed) addCoins kept as a harmless no-op so older game code still calls safely
  function addCoins(n) {}
  function addLives(n) { _commit({ lives: state.lives + n }); }
  function addScore(n) {
    const next = state.score + n;
    _commit({ score: next, highScore: Math.max(state.highScore, next) });
  }
  function loseLife() {
    const next = state.lives - 1;
    beep(160, 0.18, "sawtooth", 0.18);
    if (next <= 0) {
      _commit({ lives: 0, screen: "gameover" });
      // Auto-restart the same game after a short GAME OVER flash
      const game = state.currentGame;
      if (_restartTimer) clearTimeout(_restartTimer);
      _restartTimer = setTimeout(() => {
        if (state.screen === "gameover") startGame(game || "invaders");
      }, 2400);
    } else {
      _commit({ lives: next });
    }
  }
  // Free-to-play: start (or restart) a game with a fresh 3 lives
  function startGame(id) {
    if (_restartTimer) { clearTimeout(_restartTimer); _restartTimer = null; }
    beep(880, 0.06); beep(1320, 0.08);
    _commit({
      currentGame: id,
      screen: "playing",
      score: 0,
      lives: 3,
    });
    return true;
  }
  function backToMenu() {
    if (_restartTimer) { clearTimeout(_restartTimer); _restartTimer = null; }
    _commit({ screen: "select", currentGame: null });
  }
  function powerUp() {
    beep(523, 0.08); setTimeout(() => beep(659, 0.08), 60);
    setTimeout(() => beep(784, 0.08), 120); setTimeout(() => beep(1046, 0.2), 180);
    _commit({ lives: state.lives + 99, turbo: true, turboMs: Date.now() + 45000 });
    // auto-disable turbo after 45s
    setTimeout(() => {
      if (state.turbo && Date.now() >= state.turboMs - 100) _commit({ turbo: false });
    }, 45000);
  }
  function reset() { _commit({ lives: 3, score: 0, screen: "select", currentGame: null, turbo: false }); }

  // =====================================================================
  // FLOATING PAGE COINS — Spawn coins anywhere; click to collect
  // =====================================================================
  function FloatingCoins() {
    const a = useArcade();
    const [coins, setCoins] = React.useState([]);
    const counterRef = React.useRef(0);

    React.useEffect(() => {
      let alive = true;
      function spawn() {
        if (!alive) return;
        setCoins((cs) => {
          const filtered = cs.filter(c => Date.now() - c.ts < 14000);
          if (filtered.length >= 4) return filtered;
          const c = {
            id: counterRef.current++,
            x: 4 + Math.random() * 92, // vw
            y: 18 + Math.random() * 68, // vh
            ts: Date.now(),
            kind: Math.random() < 0.18 ? "heart" : "coin",
          };
          return [...filtered, c];
        });
      }
      const interval = setInterval(spawn, 5200);
      // spawn one quickly
      const first = setTimeout(spawn, 1800);
      return () => { alive = false; clearInterval(interval); clearTimeout(first); };
    }, []);

    // Auto-prune expired
    React.useEffect(() => {
      const t = setInterval(() => {
        setCoins((cs) => cs.filter(c => Date.now() - c.ts < 14000));
      }, 1000);
      return () => clearInterval(t);
    }, []);

    const collect = (c) => {
      setCoins((cs) => cs.filter(cc => cc.id !== c.id));
      if (c.kind === "heart") { addLives(1); beep(880, 0.1); beep(1320, 0.12); }
      else { addCoins(1); beep(1200, 0.06); beep(1600, 0.08); }
    };

    return (
      <div aria-hidden="false" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 950 }}>
        {coins.map((c) => (
          <button
            key={c.id}
            onClick={() => collect(c)}
            title={c.kind === "heart" ? "+1 LIFE" : "+1 COIN"}
            aria-label={c.kind === "heart" ? "Extra life" : "Coin"}
            style={{
              position: "fixed",
              left: `${c.x}vw`,
              top: `${c.y}vh`,
              pointerEvents: "auto",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 6,
              animation: "floatY 2.4s ease-in-out infinite, coinDrop 280ms var(--ease-arcade)",
              filter: c.kind === "heart"
                ? "drop-shadow(0 0 12px rgba(253,20,145,0.65))"
                : "drop-shadow(0 0 12px rgba(253,177,21,0.65))",
            }}
          >
            {c.kind === "heart" ? <Heart size={40}/> : <Coin size={40}/>}
          </button>
        ))}
      </div>
    );
  }

  // =====================================================================
  // GLOBAL HUD — Persistent coins/lives indicator (top-right)
  // =====================================================================
  function GlobalHUD() {
    const a = useArcade();
    const [pulse, setPulse] = React.useState(false);
    const lastRef = React.useRef(a.lastReward);

    React.useEffect(() => {
      if (a.lastReward && a.lastReward.ts !== (lastRef.current && lastRef.current.ts)) {
        lastRef.current = a.lastReward;
        setPulse(true);
        const t = setTimeout(() => setPulse(false), 600);
        return () => clearTimeout(t);
      }
    }, [a.lastReward]);

    return (
      <div style={{
        position: "fixed",
        top: 110, right: 24,
        zIndex: 940,
        display: "flex", flexDirection: "column", gap: 8,
        pointerEvents: "none",
        fontFamily: "'Press Start 2P', monospace",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px",
          background: "rgba(11,0,20,0.85)",
          border: "1px solid var(--ragross-gold)",
          boxShadow: "var(--glow-gold-sm)",
          transform: pulse ? "scale(1.1)" : "scale(1)",
          transition: "transform 200ms var(--ease-arcade)",
        }}>
          <Coin size={14}/>
          <span style={{ color: "#fff", fontSize: 12 }}>×{String(a.coins).padStart(2, "0")}</span>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px",
          background: "rgba(11,0,20,0.85)",
          border: "1px solid var(--ragross-magenta)",
          boxShadow: "var(--glow-magenta-sm)",
          transform: pulse ? "scale(1.1)" : "scale(1)",
          transition: "transform 200ms var(--ease-arcade)",
        }}>
          <Heart size={14}/>
          <span style={{ color: "#fff", fontSize: 12 }}>×{String(a.lives).padStart(2, "0")}</span>
        </div>
        {a.turbo && (
          <div style={{
            padding: "6px 10px",
            background: "var(--ragross-gold)",
            color: "var(--fg-on-gold)",
            fontSize: 9,
            letterSpacing: "0.16em",
            textAlign: "center",
            animation: "turboBlink 600ms steps(2) infinite",
          }}>
            TURBO
          </div>
        )}
      </div>
    );
  }

  // =====================================================================
  // MINI-GAME: SPACE INVADERS
  // =====================================================================
  function gameInvaders(canvas, ctx, w, h) {
    const player = { x: w / 2, y: h - 18, w: 18, h: 10 };
    const mouseX = { val: w / 2 };
    let bullets = []; // {x, y, vy}
    let enemyBullets = [];
    let cooldown = 0;
    let enemies = [];
    let dir = 1;
    let descend = 0;
    let lastTick = performance.now();
    let bonusFlash = 0;

    function makeWave() {
      enemies = [];
      const cols = 7, rows = 3;
      const spacingX = 26, spacingY = 22;
      const startX = (w - (cols - 1) * spacingX) / 2;
      const startY = 32;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          enemies.push({
            x: startX + c * spacingX,
            y: startY + r * spacingY,
            w: 14, h: 10,
            tier: r, // for color
            alive: true,
          });
        }
      }
    }
    makeWave();

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX.val = (e.clientX - rect.left) * (w / rect.width);
    };
    const onPointerMove = (e) => {
      e.preventDefault();
      onMove(e);
    };
    const onPointerDown = (e) => {
      e.preventDefault();
      canvas.setPointerCapture?.(e.pointerId);
      onMove(e);
      if (cooldown <= 0) fire();
    };
    const onClick = () => {
      if (cooldown <= 0) fire();
    };
    const onKey = (e) => {
      if (e.key === " " || e.key === "ArrowUp") { e.preventDefault(); if (cooldown <= 0) fire(); }
      if (e.key === "ArrowLeft")  { e.preventDefault(); mouseX.val -= 12; }
      if (e.key === "ArrowRight") { e.preventDefault(); mouseX.val += 12; }
    };

    function fire() {
      const turbo = get().turbo;
      bullets.push({ x: player.x, y: player.y - 6, vy: -4.2 });
      if (turbo) {
        bullets.push({ x: player.x - 5, y: player.y - 4, vy: -4 });
        bullets.push({ x: player.x + 5, y: player.y - 4, vy: -4 });
      }
      cooldown = turbo ? 12 : 22;
      beep(turbo ? 1100 : 800, 0.03, "square", 0.06);
    }

    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("click", onClick);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    window.addEventListener("keydown", onKey);

    let frame = 0;
    function update() {
      frame++;
      // Player
      player.x += (mouseX.val - player.x) * 0.25;
      player.x = Math.max(player.w / 2, Math.min(w - player.w / 2, player.x));
      cooldown--;

      // Auto fire baseline every ~600ms / 250ms turbo
      const autoEvery = get().turbo ? 18 : 42;
      if (frame % autoEvery === 0) fire();

      // Bullets
      bullets.forEach((b) => b.y += b.vy);
      bullets = bullets.filter((b) => b.y > -8);

      // Enemies advance
      const aliveEnemies = enemies.filter(e => e.alive);
      if (aliveEnemies.length === 0) {
        makeWave();
        addScore(50);
        bonusFlash = 30;
      }

      let hitEdge = false;
      const speed = 0.4 + Math.min(2.2, (1 - aliveEnemies.length / 21) * 2);
      aliveEnemies.forEach((e) => {
        e.x += dir * speed;
        if (e.x < 8 || e.x > w - 8) hitEdge = true;
      });
      if (hitEdge) {
        dir *= -1;
        aliveEnemies.forEach((e) => { e.y += 8; });
      }

      // Random enemy shot
      if (aliveEnemies.length && Math.random() < 0.022 + aliveEnemies.length * 0.002) {
        const shooter = aliveEnemies[Math.floor(Math.random() * aliveEnemies.length)];
        enemyBullets.push({ x: shooter.x, y: shooter.y + shooter.h / 2 + 2, vy: 2.2 });
      }
      enemyBullets.forEach((b) => b.y += b.vy);
      enemyBullets = enemyBullets.filter((b) => b.y < h + 8);

      // Collisions: player bullets → enemies
      bullets.forEach((b) => {
        enemies.forEach((e) => {
          if (!e.alive) return;
          if (Math.abs(b.x - e.x) < e.w / 2 + 2 && Math.abs(b.y - e.y) < e.h / 2 + 2) {
            e.alive = false;
            b.y = -100;
            beep(440 + Math.random() * 200, 0.05, "square", 0.08);
            addScore(10 + e.tier * 5);
          }
        });
      });

      // Collisions: enemy bullets → player
      enemyBullets.forEach((b) => {
        if (Math.abs(b.x - player.x) < player.w / 2 + 2 && Math.abs(b.y - player.y) < player.h / 2 + 2) {
          b.y = h + 50;
          loseLife();
        }
      });

      // Enemy reaches bottom = also a life lost
      enemies.forEach((e) => {
        if (e.alive && e.y > h - 32) {
          e.alive = false;
          loseLife();
        }
      });

      if (bonusFlash > 0) bonusFlash--;
    }

    function draw() {
      // Background
      ctx.fillStyle = "#04001a";
      ctx.fillRect(0, 0, w, h);
      // pixel stars
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      for (let i = 0; i < 32; i++) {
        const sx = (i * 73) % w;
        const sy = (i * 137 + frame * 0.5) % h;
        ctx.fillRect(sx, sy, 1, 1);
      }

      // Player ship — magenta pixel shape
      ctx.fillStyle = "#FD1491";
      // body
      ctx.fillRect(player.x - 7, player.y - 2, 14, 6);
      ctx.fillRect(player.x - 9, player.y, 18, 4);
      // cockpit
      ctx.fillStyle = "#00E8CB";
      ctx.fillRect(player.x - 2, player.y - 5, 4, 3);

      // Bullets
      ctx.fillStyle = "#FDB115";
      bullets.forEach((b) => ctx.fillRect(b.x - 1, b.y - 4, 2, 6));
      // Enemy bullets
      ctx.fillStyle = "#FD1491";
      enemyBullets.forEach((b) => ctx.fillRect(b.x - 1, b.y - 3, 2, 5));

      // Enemies — simple pixel sprite per tier
      enemies.forEach((e) => {
        if (!e.alive) return;
        const c = e.tier === 0 ? "#00E8CB" : e.tier === 1 ? "#FDB115" : "#4FFD15";
        const wig = Math.floor(frame / 14) % 2; // wiggle
        ctx.fillStyle = c;
        ctx.fillRect(e.x - 6, e.y - 4, 12, 6);
        ctx.fillRect(e.x - 4, e.y - 6, 8, 2);
        ctx.fillRect(e.x - 5 + wig, e.y + 3, 2, 2);
        ctx.fillRect(e.x + 3 - wig, e.y + 3, 2, 2);
        // eyes
        ctx.fillStyle = "#15001E";
        ctx.fillRect(e.x - 3, e.y - 3, 2, 2);
        ctx.fillRect(e.x + 1, e.y - 3, 2, 2);
      });

      if (bonusFlash > 0) {
        ctx.fillStyle = `rgba(253,177,21,${bonusFlash / 40})`;
        ctx.font = "bold 12px 'Press Start 2P'";
        ctx.textAlign = "center";
        ctx.fillText("WAVE CLEAR +50", w / 2, h / 2);
      }
    }

    function cleanup() {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("click", onClick);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("keydown", onKey);
    }
    return { update, draw, cleanup };
  }

  // =====================================================================
  // MINI-GAME: OWL CATCHER
  // =====================================================================
  function gameCatcher(canvas, ctx, w, h) {
    const paddle = { x: w / 2, w: 44, h: 8, y: h - 14 };
    const mouseX = { val: w / 2 };
    let items = []; // {x, y, vy, type}
    let frame = 0;
    let lastDrop = 0;

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX.val = (e.clientX - rect.left) * (w / rect.width);
    };
    const onPointerMove = (e) => {
      e.preventDefault();
      onMove(e);
    };
    const onPointerDown = (e) => {
      e.preventDefault();
      canvas.setPointerCapture?.(e.pointerId);
      onMove(e);
    };
    const onKey = (e) => {
      if (e.key === "ArrowLeft")  { e.preventDefault(); mouseX.val -= 14; }
      if (e.key === "ArrowRight") { e.preventDefault(); mouseX.val += 14; }
    };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    window.addEventListener("keydown", onKey);

    function spawn() {
      const r = Math.random();
      let type = "coin";
      if (r < 0.18) type = "skull";
      else if (r < 0.38) type = "owl";
      else if (r < 0.55) type = "heart";
      items.push({
        x: 10 + Math.random() * (w - 20),
        y: -10,
        vy: 1.4 + Math.random() * 1.4 + (get().score / 200),
        type,
      });
    }

    function update() {
      frame++;
      paddle.x += (mouseX.val - paddle.x) * 0.3;
      paddle.x = Math.max(paddle.w / 2, Math.min(w - paddle.w / 2, paddle.x));

      const interval = Math.max(28, 70 - Math.floor(get().score / 50) * 4);
      if (frame - lastDrop > interval) {
        spawn();
        lastDrop = frame;
      }

      items.forEach((it) => it.y += it.vy);

      // Collide with paddle
      items.forEach((it) => {
        if (it.dead) return;
        if (it.y > paddle.y - 6 && it.y < paddle.y + paddle.h + 4
            && Math.abs(it.x - paddle.x) < paddle.w / 2 + 6) {
          it.dead = true;
          if (it.type === "coin")  { addScore(10); addCoins(1); beep(1200, 0.05); }
          if (it.type === "owl")   { addScore(25);             beep(1320, 0.06); beep(1760, 0.08); }
          if (it.type === "heart") { addScore(15); addLives(1); beep(880, 0.07); beep(1320, 0.09); }
          if (it.type === "skull") { loseLife(); }
        }
      });
      items = items.filter((it) => !it.dead && it.y < h + 14);
    }

    function draw() {
      // BG
      ctx.fillStyle = "#04001a";
      ctx.fillRect(0, 0, w, h);
      // grid floor lines
      ctx.strokeStyle = "rgba(253,20,145,0.15)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 4; i++) {
        const yy = h - 4 - i * 8;
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(w, yy); ctx.stroke();
      }

      // Paddle (cyan w/ magenta accent)
      ctx.fillStyle = "#00E8CB";
      ctx.fillRect(paddle.x - paddle.w / 2, paddle.y, paddle.w, paddle.h);
      ctx.fillStyle = "#FD1491";
      ctx.fillRect(paddle.x - paddle.w / 2, paddle.y, 4, paddle.h);
      ctx.fillRect(paddle.x + paddle.w / 2 - 4, paddle.y, 4, paddle.h);

      // Items
      items.forEach((it) => {
        if (it.dead) return;
        if (it.type === "coin") {
          ctx.fillStyle = "#FDB115";
          ctx.fillRect(it.x - 4, it.y - 4, 8, 8);
          ctx.fillStyle = "#2A003D";
          ctx.fillRect(it.x - 1, it.y - 3, 2, 6);
        } else if (it.type === "skull") {
          ctx.fillStyle = "#fff";
          ctx.fillRect(it.x - 5, it.y - 5, 10, 8);
          ctx.fillStyle = "#FD1491";
          ctx.fillRect(it.x - 3, it.y - 3, 2, 2);
          ctx.fillRect(it.x + 1, it.y - 3, 2, 2);
          ctx.fillStyle = "#15001E";
          ctx.fillRect(it.x - 4, it.y + 3, 2, 2);
          ctx.fillRect(it.x - 1, it.y + 3, 2, 2);
          ctx.fillRect(it.x + 2, it.y + 3, 2, 2);
        } else if (it.type === "heart") {
          ctx.fillStyle = "#FD1491";
          ctx.fillRect(it.x - 4, it.y - 3, 3, 2);
          ctx.fillRect(it.x + 1, it.y - 3, 3, 2);
          ctx.fillRect(it.x - 5, it.y - 1, 10, 3);
          ctx.fillRect(it.x - 3, it.y + 2, 6, 2);
          ctx.fillRect(it.x - 1, it.y + 4, 2, 1);
        } else if (it.type === "owl") {
          ctx.fillStyle = "#FDB115";
          // body
          ctx.fillRect(it.x - 5, it.y - 3, 10, 8);
          // ears
          ctx.fillStyle = "#FD1491";
          ctx.fillRect(it.x - 5, it.y - 5, 2, 2);
          ctx.fillRect(it.x + 3, it.y - 5, 2, 2);
          // eyes
          ctx.fillStyle = "#fff";
          ctx.fillRect(it.x - 3, it.y - 1, 2, 2);
          ctx.fillRect(it.x + 1, it.y - 1, 2, 2);
          ctx.fillStyle = "#15001E";
          ctx.fillRect(it.x - 3, it.y - 1, 1, 1);
          ctx.fillRect(it.x + 1, it.y - 1, 1, 1);
        }
      });
    }

    function cleanup() {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("keydown", onKey);
    }
    return { update, draw, cleanup };
  }

  // =====================================================================
  // CANVAS HOST
  // =====================================================================
  function GameCanvas({ gameId }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
      const cvs = ref.current;
      if (!cvs) return;
      const w = cvs.width  = 280;
      const h = cvs.height = 240;
      const ctx = cvs.getContext("2d");
      ctx.imageSmoothingEnabled = false;

      let game;
      if (gameId === "invaders") game = gameInvaders(cvs, ctx, w, h);
      else if (gameId === "catcher") game = gameCatcher(cvs, ctx, w, h);
      if (!game) return;

      let raf;
      let alive = true;
      const loop = () => {
        if (!alive) return;
        if (get().screen !== "playing") { alive = false; return; }
        game.update();
        game.draw();
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      return () => {
        alive = false;
        cancelAnimationFrame(raf);
        game.cleanup && game.cleanup();
      };
    }, [gameId]);

    return (
      <canvas
        ref={ref}
        style={{
          width: "100%",
          height: "100%",
          imageRendering: "pixelated",
          display: "block",
          cursor: "crosshair",
          touchAction: "none",
          userSelect: "none",
        }}
      />
    );
  }

  // =====================================================================
  // ARCADE MACHINE (in the CRT screen)
  // =====================================================================
  const games = [
    { id: "invaders", name: "SPACE INVADERS", desc: "Mueve · Auto-fire", icon: Joystick, color: "var(--ragross-cyan)"   },
    { id: "catcher",  name: "OWL CATCHER",    desc: "Atrapa · Esquiva",  icon: Star,     color: "var(--ragross-gold)"   },
  ];

  function ArcadeMachine() {
    const a = useArcade();
    const t = useT();
    const [selected, setSelected] = React.useState("invaders");

    // Header HUD
    const hudStyle = {
      position: "absolute",
      top: 8, left: 10, right: 10,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9,
      letterSpacing: "0.12em",
      zIndex: 4,
      pointerEvents: "none",
    };

    const screen = a.screen;
    const showHud = screen === "playing" || screen === "gameover";

    return (
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "stretch", justifyContent: "center" }}>
        {showHud && (
          <div style={hudStyle}>
            <span style={{ color: "var(--ragross-gold)", display: "flex", alignItems: "center", gap: 6 }}>
              HI {String(a.highScore).padStart(5, "0")}
            </span>
            <span style={{ color: "#fff" }}>SCORE {String(a.score).padStart(5, "0")}</span>
            <span style={{ color: "var(--ragross-magenta)", display: "flex", alignItems: "center", gap: 4 }}>
              {Array.from({ length: Math.min(a.lives, 4) }).map((_, i) => (
                <Heart key={i} size={11}/>
              ))}
              {a.lives > 4 && <span style={{ marginLeft: 2 }}>×{a.lives}</span>}
            </span>
          </div>
        )}

        {/* SELECT SCREEN */}
        {screen === "select" && (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            width: "100%", padding: 14, gap: 12,
            position: "relative", zIndex: 3,
          }} className="arcade-select-screen">
            <div style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: 10,
              letterSpacing: "0.20em",
              color: "var(--ragross-gold)",
              textAlign: "center",
            }}>
              ★ RAGROSS ARCADE ★
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4 }} className="arcade-game-list">
              {games.map((g) => {
                const Icon = g.icon;
                const isSel = selected === g.id;
                return (
                  <button
                    key={g.id}
                    className="arcade-game-card"
                    onClick={() => setSelected(g.id)}
                    style={{
                      flex: 1,
                      padding: "10px 8px",
                      background: isSel ? "rgba(0,232,203,0.10)" : "var(--ragross-purple-night)",
                      border: `2px solid ${isSel ? g.color : "rgba(255,255,255,0.10)"}`,
                      color: "#fff",
                      cursor: "pointer",
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: 8,
                      letterSpacing: "0.10em",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 6,
                      boxShadow: isSel ? `3px 3px 0 ${g.color}` : "none",
                      transition: "all 160ms var(--ease-arcade)",
                    }}
                  >
                    <Icon size={20}/>
                    <span style={{ color: g.color, fontSize: 8 }}>{g.name}</span>
                    <span style={{ color: "var(--fg-3)", fontSize: 7 }}>{g.desc}</span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => startGame(selected)}
              style={{
                marginTop: 8,
                padding: "12px 18px",
                background: "var(--ragross-magenta)",
                color: "#fff",
                border: "2px solid #fff",
                fontFamily: "'Press Start 2P', monospace",
                fontSize: 10, letterSpacing: "0.18em",
                cursor: "pointer",
                boxShadow: "4px 4px 0 var(--ragross-cyan), var(--glow-magenta-md)",
                animation: "insertBlink 1.4s ease-in-out infinite",
              }}
            >
              ▸ PRESS START
            </button>

            <div style={{
              fontFamily: "'VT323', monospace",
              fontSize: 14,
              color: "var(--fg-3)",
              textAlign: "center",
              marginTop: 2,
            }}>
              <span style={{ color: "var(--ragross-magenta)" }}>3</span> vidas · juego libre · mueve con el mouse
            </div>
          </div>
        )}

        {/* PLAYING */}
        {screen === "playing" && a.currentGame && (
          <div style={{
            position: "absolute", inset: 0,
            paddingTop: 28, paddingBottom: 4, paddingLeft: 14, paddingRight: 14,
          }} className="arcade-play-area">
            <GameCanvas gameId={a.currentGame} />
          </div>
        )}

        {/* GAME OVER */}
        {screen === "gameover" && (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            width: "100%", padding: 14, gap: 10,
            position: "relative", zIndex: 3, textAlign: "center",
          }}>
            <div style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: 14, letterSpacing: "0.20em",
              color: "var(--ragross-magenta)",
            }}>
              GAME OVER
            </div>
            <div style={{ fontFamily: "'VT323', monospace", fontSize: 22, color: "#fff", lineHeight: 1.2 }}>
              SCORE · <span style={{ color: "var(--ragross-gold)" }}>{a.score}</span>
            </div>
            <div style={{ fontFamily: "'Press Start 2P', monospace", fontSize: 8, color: "var(--ragross-cyan)", letterSpacing: "0.16em" }}>
              HI · {a.highScore}
            </div>
            <div style={{ fontFamily: "'VT323', monospace", fontSize: 15, color: "var(--fg-3)" }}>
              reiniciando<span className="blink">...</span>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button
                onClick={() => startGame(a.currentGame || "invaders")}
                style={{
                  padding: "10px 14px",
                  background: "var(--ragross-magenta)", color: "#fff",
                  border: "2px solid #fff",
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: 9, letterSpacing: "0.16em",
                  cursor: "pointer",
                  boxShadow: "3px 3px 0 var(--ragross-cyan)",
                }}
              >
                ▸ RETRY
              </button>
              <button
                onClick={() => backToMenu()}
                style={{
                  padding: "10px 14px",
                  background: "transparent", color: "var(--ragross-cyan)",
                  border: "2px solid var(--ragross-cyan)",
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: 9, letterSpacing: "0.16em",
                  cursor: "pointer",
                }}
              >
                MENÚ
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -- exports
  return {
    useArcade, addCoins, addLives, addScore, loseLife,
    startGame, backToMenu, powerUp, reset, get,
    ArcadeMachine,
  };
})();

export default Arcade;
